import { Platform } from 'react-native';
import {
  readWebStorage,
  removeWebStorage,
  writeWebStorage,
} from './web-storage';

export type OnboardingAiPlanCheckout = {
  plan: 'plus' | 'pro';
  createdAt: number;
  userId?: string | null;
  planSet?: 'individual';
  billingInterval?: 'monthly';
};

const STORAGE_KEY = 'tabbio:onboarding:ai-plan-checkout';
const CONFIRMED_KEY = 'tabbio:onboarding:ai-plan-checkout-confirmed';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function writePendingOnboardingAiPlanCheckout(
  plan: OnboardingAiPlanCheckout['plan'],
  options?: { userId?: string | null },
) {
  if (Platform.OS !== 'web') return;
  writeWebStorage(
    STORAGE_KEY,
    JSON.stringify({
      plan,
      createdAt: Date.now(),
      userId: options?.userId ?? null,
      planSet: 'individual',
      billingInterval: 'monthly',
    } satisfies OnboardingAiPlanCheckout),
  );
}

export function readPendingOnboardingAiPlanCheckout(): OnboardingAiPlanCheckout | null {
  if (Platform.OS !== 'web') return null;
  const raw = readWebStorage(STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<OnboardingAiPlanCheckout>;
    if (parsed.plan !== 'plus' && parsed.plan !== 'pro') {
      clearPendingOnboardingAiPlanCheckout();
      return null;
    }
    if (
      typeof parsed.createdAt !== 'number' ||
      Date.now() - parsed.createdAt > MAX_AGE_MS
    ) {
      clearPendingOnboardingAiPlanCheckout();
      return null;
    }
    return {
      plan: parsed.plan,
      createdAt: parsed.createdAt,
      userId: typeof parsed.userId === 'string' ? parsed.userId : null,
      planSet: 'individual',
      billingInterval: 'monthly',
    };
  } catch {
    clearPendingOnboardingAiPlanCheckout();
    return null;
  }
}

export function pendingOnboardingAiPlanCheckoutMatchesUser(
  checkout: OnboardingAiPlanCheckout | null,
  userId: string | null | undefined,
) {
  return !checkout?.userId || checkout.userId === userId;
}

export function clearPendingOnboardingAiPlanCheckout() {
  if (Platform.OS !== 'web') return;
  removeWebStorage(STORAGE_KEY);
}

/**
 * Remembers that a checkout return was confirmed and its pending checkout
 * cleared. The `?checkout=success` entry stays in the browser history, so a
 * later Back onto it must read as a plain visit, not a checkout we could not
 * find ("Checkout failed" shown to someone who just paid).
 */
export function markOnboardingAiPlanCheckoutConfirmed() {
  if (Platform.OS !== 'web') return;
  writeWebStorage(CONFIRMED_KEY, String(Date.now()));
}

export function wasOnboardingAiPlanCheckoutConfirmed(): boolean {
  if (Platform.OS !== 'web') return false;
  const confirmedAt = Number(readWebStorage(CONFIRMED_KEY));
  return Number.isFinite(confirmedAt) && Date.now() - confirmedAt <= MAX_AGE_MS;
}
